"use client";
import Link from "next/link";
import Image, { StaticImageData } from "next/image";
import logo from "@/assets/images/logo.svg";
import AICPALogo from "@/assets/images/footer/aicpa-soc-logo.svg";
import ISOLogo from "@/assets/images/footer/ISO-logo.svg";
import hippaLogoImage from "@/assets/images/footer/hippa-logo.svg";
import LinkedinIc from "@/assets/images/footer/LinkedinIC.svg";
import FacebookIc from "@/assets/images/footer/FacebbokIC.svg";
import InstagramIc from "@/assets/images/footer/InstaGramIc.svg";
import TwiteerIc from "@/assets/images/footer/TweeterIc.svg";
import YoutubeIc from "@/assets/images/footer/YoutubeIC.svg";
// import EndChips from "@/assets/images/footer/end-chips.svg";
import appleLogo from "@/assets/images/footer/apple-store.svg";
import playStoreLogo from "@/assets/images/footer/play-store.svg";
import highPerformance from "@/assets/images/footer/high-performer.svg";
import userEnggagement from "@/assets/images/footer/user-engagement.svg";
import {
  LoginHubspotButtonUrl,
  SignUpHubspotButtonUrl,
} from "@/utils/hubspot-contants";

// for common text
const CommonTexts = ({
  content,
  style,
  fontWeightClass,
}: {
  content: string;
  style: string;
  fontWeightClass?: string;
}) => {
  return (
    <p
      style={{ color: style }}
      className={`text-[13px] ${fontWeightClass ? fontWeightClass : "font-normal"} leading-[26px]`}
    >
      {content}
    </p>
  );
};

// For Logo
const FooterLogos = ({ LogoImg, alt }: { LogoImg: StaticImageData; alt: string }) => {
  return <Image src={LogoImg} quality={100} alt={alt} />;
};

// Footer Item Title
const FootersTitle = ({ text }: { text: string }) => {
  return <p className="text-[14px] font-bold text-[#202020]">{text}</p>;
};

// This Is Main
const Footer = () => {
  // footer data
  const menuData = [
    {
      category: "Products",
      items: [
        {
          title: "Work Management",
          linkUrl: "/work-management",
        },
        {
          title: "Enterprise Search",
          linkUrl: "/ai/enterprise-search",
        },
        {
          title: "Project & Sprints",
          linkUrl: "/features/projects",
        },
        {
          title: "Portfolio",
          linkUrl: "/features/portfolio",
        },
        {
          title: "Tasks",
          linkUrl: "/features/tasks",
        },
        {
          title: "Automations",
          linkUrl: "/features/automations",
        },
        {
          title: "Goal & OKRs",
          linkUrl: "/features/goals",
        },
        {
          title: "Docs",
          linkUrl: "/features/docs",
        },
        {
          title: "Chats",
          linkUrl: "/features/chats",
        },
        {
          title: "Forms",
          linkUrl: "/features/forms",
        },
        {
          title: "Track Time",
          linkUrl: "/features/track-time",
        },
      ],
    },
    {
      category: "Kroolo AI",
      items: [
        {
          title: "AI Agents",
          linkUrl: "/ai/ai-agent",
        },
        {
          title: "Kroolo AI",
          linkUrl: "/features/ai",
        },
        {
          title: "Grammar Checker",
          linkUrl: "/ai-tools/grammar-checker",
        },
        {
          title: "Paraphraser",
          linkUrl: "/ai-tools/paraphraser",
        },
        {
          title: "Summarizer",
          linkUrl: "/ai-tools/summarizer",
        },
        {
          title: "Translator",
          linkUrl: "/ai-tools/translator",
        },
        {
          title: "Chat with PDF",
          linkUrl: "/ai-tools/chat-with-pdf",
        },
      ],
    },
    {
      category: "Solutions",
      items: [
        {
          title: "Project management",
          linkUrl: "/solutions/project-management",
        },
        {
          title: "Product Management",
          linkUrl: "/solutions/product-management",
        },
        {
          title: "Marketing",
          linkUrl: "/solutions/marketing",
        },
        {
          title: "Engineering",
          linkUrl: "/solutions/engineering",
        },
        {
          title: "Support & Business Operations",
          linkUrl: "/solutions/business-operations",
        },
        {
          title: "HR & Recruitment",
          linkUrl: "/solutions/hr-recruitment",
        },
        {
          title: "Education",
          linkUrl: "/solutions/k12-education",
        },
        {
          title: "Program Management",
          linkUrl: "/solutions/program-management",
        },
      ],
    },
    {
      category: "Industries",
      items: [
        {
          title: "Agencies",
          linkUrl: "/industries/agencies",
        },
        {
          title: "Financial Services",
          linkUrl: "/industries/financial-services",
        },
        {
          title: "Retail",
          linkUrl: "/industries/retail",
        },
        {
          title: "Manufacturing",
          linkUrl: "/industries/manufacturing",
        },
        {
          title: "Education",
          linkUrl: "/industries/education",
        },
        {
          title: "Real Estate",
          linkUrl: "/industries/real-estate",
        },
        {
          title: "IT Consulting and Services",
          linkUrl: "/industries/it-consulting-and-services",
        },
        {
          title: "Healthcare",
          linkUrl: "/industries/healthcare",
        },
        {
          title: "Travel and Hospitality",
          linkUrl: "/industries/travel-and-hospitality",
        },
        {
          title: "Media",
          linkUrl: "/industries/media",
        },
      ],
    },
    {
      category: "Resources",
      items: [
        {
          title: "Help center",
          linkUrl: "https://help.kroolo.com/",
          openSeparate: true,
        },
        {
          title: "Blogs",
          linkUrl: "/blog",
        },
        {
          title: "Videos",
          linkUrl: "/videos",
        },
        {
          title: "Changelog",
          linkUrl: "https://kroolo.featurebase.app/changelog",
          openSeparate: true,
        },
        {
          title: "Feedback",
          linkUrl: "https://kroolo.featurebase.app",
          openSeparate: true,
        },
      ],
    },
    {
      category: "Compare",
      items: [
        {
          title: "vs Asana",
          linkUrl: "/compare/kroolo-vs-asana",
        },
        {
          title: "vs Azure DevOps",
          linkUrl: "/compare/kroolo-vs-azure-devops",
        },
        {
          title: "vs ClickUp",
          linkUrl: "/compare/kroolo-vs-clickup",
        },
        {
          title: "vs Jira Cloud",
          linkUrl: "/compare/kroolo-vs-jira",
        },
        {
          title: "vs Monday",
          linkUrl: "/compare/kroolo-vs-monday",
        },
        {
          title: "vs MS Planner",
          linkUrl: "/compare/kroolo-vs-microsoft-planner",
        },
        {
          title: "vs Smartsheet",
          linkUrl: "/compare/kroolo-vs-smartsheet",
        },
        {
          title: "vs Glean",
          linkUrl: "/compare/kroolo-vs-glean",
        },
        {
          title: "vs Microsoft Copilot",
          linkUrl: "/compare/kroolo-vs-microsoft-copilot",
        },
      ],
    },
  ];
  const contactLinks = {
    items: [
      {
        title: "Book a Demo",
        linkUrl: "/book-demo",
        openSeparate: false,
      },
      {
        title: "Support",
        linkUrl: "/contact-support",
      },
      {
        title: "Pricing",
        linkUrl: "/pricing",
      },
      {
        title: "Login",
        linkUrl: LoginHubspotButtonUrl,
      },
      {
        title: "SignUp",
        linkUrl: SignUpHubspotButtonUrl,
      },
      {
        title: "Download App",
        linkUrl: "/download",
      },
    ],
  };

  // social links
  const SocialLinks = [
    { icon: LinkedinIc, Link: "https://www.linkedin.com/company/getkroolo/", alt: "Follow Kroolo on LinkedIn" },
    { icon: FacebookIc, Link: "https://www.facebook.com/people/Kroolo/61553808299270/", alt: "Follow Kroolo on Facebook" },
    { icon: InstagramIc, Link: "https://www.instagram.com/getkroolo", alt: "Follow Kroolo on Instagram" },
    { icon: TwiteerIc, Link: "https://www.twitter.com/getkroolo", alt: "Follow Kroolo on X (Twitter)" },
    { icon: YoutubeIc, Link: "https://youtube.com/@getkroolo?si=z2hD5yQsZ7h6jhdw", alt: "Subscribe to Kroolo on YouTube" },
  ];

  return (
    <footer className="bg-[#F8F8F8]">
      <div className="relative py-10 xl:py-10">
        <div className="container">
          <div className="relative flex flex-wrap gap-10 pb-5 xlg:flex-col xlg:gap-[60px] xl:flex-row xl:flex-nowrap">
            {/* main content wrapper */}
            <div className="lg:mx-0 lg:w-fit lg:min-w-[200px]">
              <Link href={"/"} className="block w-[130px]">
                <FooterLogos LogoImg={logo} alt="Kroolo - AI-powered productivity management platform" />
              </Link>
              <div className="mt-[18px] flex gap-[12px] lg:mx-0">
                <div className="h-[50px] w-[50px] lg:h-[72px] lg:w-[72px]">
                  <FooterLogos LogoImg={highPerformance} alt="G2 High Performer - Kroolo" />
                </div>
                <div className="h-[50px] w-[50px] lg:h-[78px] lg:w-[78px]">
                  <FooterLogos LogoImg={userEnggagement} alt="G2 User Engagement - Kroolo" />
                </div>
              </div>
              <div className="mt-5 flex gap-3">
                {SocialLinks.map((item) => (
                  <Link
                    className="flex h-8 w-8 items-center justify-center"
                    key={item.Link}
                    href={item.Link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      src={item.icon}
                      alt={item.alt}
                      quality={100}
                      className="h-full w-full"
                    />
                  </Link>
                ))}
              </div>
              <div className="mt-5 lg:mt-[30px]">
                <div className="z-10 grid w-full grid-cols-2 justify-between gap-8 xlg:flex xlg:gap-[40px]">
                  <ul className="flex flex-col gap-y-2">
                    {contactLinks.items.map((singleLink, i) => (
                      <li key={i}>
                        <Link
                          href={singleLink.linkUrl}
                          className="w-fit text-[12px] font-normal text-[#464646] duration-300 ease-in-out"
                          target={singleLink?.openSeparate ? "_blank" : "_self"}
                        >
                          {singleLink.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="z-10 grid w-full grid-cols-2 justify-between gap-8 xlg:flex xlg:gap-[40px]">
              {menuData.map((section, index) => (
                <div key={index} className="flex flex-col gap-y-[12px]">
                  <FootersTitle text={section.category} />
                  <ul className="flex flex-col gap-y-2">
                    {section.items.map((singleLink, i) => (
                      <li key={i}>
                        <Link
                          href={singleLink.linkUrl}
                          className="w-fit text-[12px] font-normal text-[#464646] duration-300 ease-in-out"
                          target={singleLink.openSeparate ? "_blank" : "_self"}
                        >
                          {singleLink.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t border-[#DDDDDD] pt-5 text-[12px] text-[#464646]">
            <div className="flex items-center gap-2">
              <span>Get Kroolo mobile app</span>
              <Link
                href={"https://apps.apple.com/in/app/kroolo/id6740263578"}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image src={appleLogo} alt="Download Kroolo on App Store" className="h-8 w-auto" />
              </Link>
              <Link
                href={"https://play.google.com/store/apps/details?id=com.kroolo.app"}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image src={playStoreLogo} alt="Get Kroolo on Google Play Store" className="h-8 w-auto" />
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <Link className="duration-500 ease-in-out hover:text-[#202020]" href={"/legal/terms-of-service"}>
                Terms of Service
              </Link>
              <Link className="duration-500 ease-in-out hover:text-[#202020]" href={"/legal/privacy-policy"}>
                Privacy
              </Link>
              <Link className="duration-500 ease-in-out hover:text-[#202020]" href={"/legal/security"}>
                Security
              </Link>
              <Link className="duration-500 ease-in-out hover:text-[#202020]" href={"/legal/cookie-policy"}>
                Cookies
              </Link>
              <Link className="duration-500 ease-in-out hover:text-[#202020]" href={"/legal/ai-policy"}>
                AI Policy
              </Link>
              <Link className="duration-500 ease-in-out hover:text-[#202020]" href={"/legal/msa"}>
                Master Services Agreement
              </Link>
              <Link className="duration-500 ease-in-out hover:text-[#202020]" href={"/legal/dpa"}>
                DPA
              </Link>
            </div>
          </div>
        </div>
        {/* <div className="absolute bottom-0 left-0 z-0 w-full">
          <Image
            src={EndChips}
            alt="Kroolo footer decoration"
            width={1920}
            height={200}
            draggable={false}
            className="h-auto w-full object-cover"
            style={{ opacity: 0.3 }}
          />
        </div> */}
      </div>
    </footer>
  );
};

export default Footer;
